<template>
  <!-- DetectionZoneData is the COMPLETE per-zone view — so weather shows for EVERY
       zone, including an honest "no clear weather link" result (a flat curve is data).
       Works for tripwire zones too (crossings, not visits). Gating happens on the
       curated surfaces (Home / Intelligence / Explore overview), not here. -->
  <div class="zwx" :class="{ flat: wx.band === 'none' }">
    <div class="zwx-head">
      <span class="zwx-ico"><i class="bi bi-cloud-sun"></i></span>
      <span class="zwx-title">{{ $t('demo.zoneWeather.title') }}</span>
      <span class="zwx-band" :class="wx.band" :title="bandTip">{{ bandLabel }}</span>
    </div>
    <p class="zwx-lead">{{ wx.lead }}</p>

    <div class="zwx-body">
      <div class="zwx-curve">
        <div class="zwx-curve-title">{{ $t('demo.zoneWeather.byTemperature', { unit: unitLabel }) }}</div>
        <div class="zwx-bars">
          <div v-for="b in wx.curve" :key="b.label" class="zwx-col">
            <div class="zwx-bar" :class="b.tone" :style="{ height: (b.h * 0.7) + 'px' }"></div>
            <div class="zwx-lbl">{{ $t(`demo.zoneWeather.curve.${b.label}`) }}</div>
          </div>
        </div>
      </div>

      <div class="zwx-side">
        <template v-if="wx.signals.length">
          <div v-for="s in wx.signals" :key="s.var" class="zwx-sig">
            <i class="bi" :class="s.icon"></i>
            <span class="zwx-sig-txt">{{ s.text }}</span>
            <b :class="s.dir">{{ s.dir === 'up' ? '+' : '−' }}{{ s.pct }}%</b>
          </div>
          <div v-if="wx.vsStore" class="zwx-vs"><i class="bi bi-bar-chart-steps"></i> {{ wx.vsStore }}</div>
        </template>
        <div v-else class="zwx-none">
          <i class="bi bi-check2-circle"></i>
          {{ $t('demo.zoneWeather.noneChecked', { unit: unitLower }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { withNl } from '@/demo/demoLang'

const { t } = useI18n()

const props = defineProps({
  zoneName: { type: String, required: true },
  // Tripwire zones count crossings, not visits — label accordingly.
  useCrossings: { type: Boolean, default: false },
})

// Per-zone weather profiles (demo). Zones NOT listed fall back to the honest
// FLAT result — no card is hidden here (this is the complete data view).
// Production reads scan_company_weather_hourly zone-share signals.
const BY_ZONE = {
  'Garden Utilities': withNl({
    band: 'established',
    lead: 'A weather-led aisle — traffic here swings hard with the sky, far more than the store overall.',
    curve: [
      { label: 'Cold', h: 38, tone: 'cold' }, { label: 'Cool', h: 58 },
      { label: 'Mild', h: 82 }, { label: 'Warm', h: 100, tone: 'hot' },
    ],
    signals: [
      withNl({ var: 'temp', icon: 'bi-thermometer-sun', dir: 'up', pct: 34, text: 'busier as it warms' }, { text: 'drukker als het warmer wordt' }),
      withNl({ var: 'precip', icon: 'bi-cloud-rain', dir: 'down', pct: 20, text: 'quieter when wet' }, { text: 'rustiger bij regen' }),
    ],
    vsStore: 'Reacts ~4× more strongly to warmth than the store overall.',
  }, {
    lead: 'Een gangpad dat op het weer draait: het aantal bezoekers schommelt hier sterk met de lucht, veel meer dan in de hele winkel.',
    vsStore: 'Reageert ~4× sterker op warmte dan de winkel als geheel.',
  }),
  'Power Tools': withNl({
    band: 'established',
    lead: 'Bucks the store — a bigger share of shoppers head here in poor weather (indoor projects).',
    curve: [
      { label: 'Cold', h: 100, tone: 'cold' }, { label: 'Cool', h: 86 },
      { label: 'Mild', h: 72 }, { label: 'Warm', h: 60, tone: 'hot' },
    ],
    signals: [withNl({ var: 'precip', icon: 'bi-cloud-rain', dir: 'up', pct: 18, text: 'busier on wet days' }, { text: 'drukker op natte dagen' })],
    vsStore: 'Moves opposite the store — up when the weather keeps shoppers indoors.',
  }, {
    lead: 'Gaat tegen de winkel in: bij slecht weer komt een groter deel van je bezoekers hier (klussen binnen).',
    vsStore: 'Beweegt tegengesteld aan de winkel: omhoog als het weer bezoekers binnen houdt.',
  }),
  'Promoted Articles Zone': withNl({
    band: 'established',
    lead: 'Heat-shy — fewer shoppers linger at the front promo on hot afternoons.',
    curve: [
      { label: 'Cold', h: 92, tone: 'cold' }, { label: 'Cool', h: 86 },
      { label: 'Mild', h: 76 }, { label: 'Warm', h: 60, tone: 'hot' },
    ],
    signals: [withNl({ var: 'temp', icon: 'bi-thermometer-sun', dir: 'down', pct: 11, text: 'loses share in the heat' }, { text: 'verliest aandeel in de hitte' })],
    vsStore: 'A share effect — it fades in heat even when overall footfall holds.',
  }, {
    lead: 'Houdt niet van hitte: op warme middagen blijven minder bezoekers bij de promo vooraan staan.',
    vsStore: 'Een effect op het aandeel: het zakt in de hitte, ook als het totaal aantal winkelbezoekers gelijk blijft.',
  }),
}

// Honest null result — a near-flat curve (weather doesn't move this zone).
const FLAT = withNl({
  band: 'none',
  lead: 'No clear weather link — this zone stays steady whatever the sky.',
  curve: [
    { label: 'Cold', h: 80, tone: 'cold' }, { label: 'Cool', h: 84 },
    { label: 'Mild', h: 82 }, { label: 'Warm', h: 79, tone: 'hot' },
  ],
  signals: [],
  vsStore: null,
}, { lead: 'Geen duidelijk verband met het weer: deze zone blijft stabiel, wat de lucht ook doet.' })

const wx = computed(() => BY_ZONE[props.zoneName] || FLAT)
const unitLabel = computed(() => (props.useCrossings ? t('demo.zoneWeather.crossings') : t('demo.zoneWeather.visits')))
const unitLower = computed(() => (props.useCrossings ? t('demo.zoneWeather.crossingsLower') : t('demo.zoneWeather.visitsLower')))
const bandLabel = computed(() => (wx.value.band === 'established' ? t('demo.weather.confirmed')
  : wx.value.band === 'watch' ? t('demo.weather.earlySignal') : t('demo.weather.noLink')))
const bandTip = computed(() => (wx.value.band === 'established'
  ? t('demo.weather.tipConfirmed')
  : wx.value.band === 'watch' ? t('demo.zoneWeather.tipEarly')
    : t('demo.zoneWeather.tipNone')))
</script>

<style scoped>
.zwx {
  background: linear-gradient(180deg, #f3faf9, #ffffff 60%);
  border: 1px solid #d9ece9;
  border-left: 3px solid #1f9d9d;
  border-radius: 12px;
  padding: 14px 16px 16px;
}
.zwx.flat { background: #fafbfb; border-color: #e7ebea; border-left-color: #c2ccca; }
.zwx-head { display: flex; align-items: center; gap: 8px; }
.zwx-ico {
  width: 30px; height: 30px; border-radius: 8px; background: #fff; color: #178f8f;
  display: flex; align-items: center; justify-content: center; font-size: 1rem;
  box-shadow: 0 1px 3px rgba(20, 60, 55, 0.08);
}
.zwx.flat .zwx-ico { color: #8a9a97; }
.zwx-title { font-weight: 650; color: #12615f; font-size: 0.95rem; }
.zwx.flat .zwx-title { color: #556; }
.zwx-band {
  margin-left: auto; font-size: 0.72rem; padding: 3px 9px; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 4px;
}
.zwx-band.established { background: #e6f4ef; color: #2f7d4f; }
.zwx-band.watch { background: #f3f4f6; color: #6b7280; }
.zwx-band.none { background: #eef1f0; color: #7c8987; }
.zwx-lead { margin: 8px 0 12px; font-size: 0.86rem; color: #385c58; line-height: 1.45; }
.zwx.flat .zwx-lead { color: #6a7876; }

.zwx-body { display: flex; gap: 20px; align-items: flex-end; flex-wrap: wrap; }
.zwx-curve { flex: 0 0 auto; }
.zwx-curve-title { font-size: 0.74rem; color: #6b8582; margin-bottom: 6px; font-weight: 600; }
.zwx-bars { display: flex; align-items: flex-end; gap: 10px; height: 84px; }
.zwx-col { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.zwx-bar { width: 26px; border-radius: 5px 5px 2px 2px; background: #9fd0cb; }
.zwx-bar.cold { background: #7fb0d8; }
.zwx-bar.hot { background: #e0a94a; }
.zwx.flat .zwx-bar, .zwx.flat .zwx-bar.cold, .zwx.flat .zwx-bar.hot { background: #cdd6d4; }
.zwx-lbl { font-size: 0.68rem; color: #6b8582; }

.zwx-side { flex: 1 1 220px; min-width: 200px; display: flex; flex-direction: column; gap: 7px; }
.zwx-sig { display: flex; align-items: center; gap: 7px; font-size: 0.84rem; color: #45605c; }
.zwx-sig .bi { color: #178f8f; }
.zwx-sig-txt { flex: 1 1 auto; }
.zwx-sig b.up { color: #2f7d4f; }
.zwx-sig b.down { color: #9a6a1c; }
.zwx-vs {
  margin-top: 4px; font-size: 0.78rem; color: #5b7a76;
  background: #eef7f5; border-radius: 8px; padding: 6px 9px;
  display: flex; align-items: center; gap: 6px;
}
.zwx-vs .bi { color: #178f8f; }
.zwx-none {
  font-size: 0.82rem; color: #6a7876; display: flex; align-items: flex-start; gap: 7px;
  background: #f1f4f3; border-radius: 8px; padding: 8px 10px;
}
.zwx-none .bi { color: #8a9a97; margin-top: 1px; }

/* Phones: stack the temperature curve above the signals so neither is cramped. */
@media (max-width: 560px) {
  .zwx-body { flex-direction: column; align-items: stretch; gap: 12px; }
  .zwx-side { min-width: 0; flex: 1 1 auto; }
  .zwx-bars { justify-content: flex-start; }
  .zwx.flat .zwx-title { font-size: 0.9rem; }
}
</style>
